import { Injectable } from '@angular/core';
import {
  HttpClient,
  HttpHeaders,
  HttpErrorResponse,
} from '@angular/common/http';
import { of, throwError, Observable } from 'rxjs';
import { map, catchError, tap } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { PromptingService } from './prompting.service';
import { SendMessageRequest } from 'src/app/models/owner-dashboard';

@Injectable({ providedIn: 'root' })
export class MessengerApi {
  private apiUrl;
  private headers = new HttpHeaders({ 'Content-Type': 'application/json' });

  constructor(
    private http: HttpClient,
    private prompting: PromptingService,
  ) {
    if ('api' in environment && 'url' in environment['api']) {
      this.apiUrl = environment['api']['url'];
    }
  }

  // My Affiliates
  getThreads(
    page: number = 0,
    itemsPerPage: number = 12,
    id: number = undefined,
    isAffiliate: boolean = false,
    errorHandler: any = undefined,
  ) {
    return this.get(
      `messenger/threads?page=${page}&itemsPerPage=${itemsPerPage}&threadId=${id}&isAffiliate=${isAffiliate}`,
      {},
      errorHandler,
    );
  }

  getThreadById(
    id: number,
    page: number = 0,
    itemsPerPage: number = 20,
    errorHandler: any = undefined,
  ) {
    return this.get(
      `messenger/threads/${id}?page=${page}&itemsPerPage=${itemsPerPage}`,
      {},
      errorHandler,
    );
  }

  getThreadByUserIds(
    userIds: number[],
    isAffiliate: boolean = false,
    errorHandler: any = undefined,
  ) {
    return this.post(
      `messenger/thread-by-users?isAffiliate=${isAffiliate}`,
      { userIds },
      errorHandler,
    );
  }
  getThreadMediaById(id: number, errorHandler: any = undefined) {
    return this.get(`messenger/thread-images/${id}`, {}, errorHandler);
  }

  getAffiliatesTotalTracking(id: number, errorHandler: any = undefined) {
    return this.get(`owner/affiliate-tracking-totals/${id}`, {}, errorHandler);
  }

  sendMessageByThreadId(
    messageBody: SendMessageRequest,
    isAffiliate: boolean = false,
    errorHandler: any = undefined,
  ) {
    return this.post(
      `messenger/threads?isAffiliate=${isAffiliate}`,
      messageBody,
      errorHandler,
    );
  }

  inviteAffiliates(
    invitationData: { emails: string[]; affiliateLinkIds: number[] },
    errorHandler: any = undefined,
  ) {
    return this.post(`owner/invite-affiliates`, invitationData, errorHandler);
  }

  uploadKajabiCsv(data: FormData, errorHandler: any = undefined) {
    return this.upload('owner/import-kajabi', data, errorHandler);
  }
  resendInvitationToAffiliates(
    invitationData: { emails: string[]; affiliateLinkIds?: number[] },
    errorHandler: any = undefined,
  ) {
    return this.post(`owner/reinvite-affiliates`, invitationData, errorHandler);
  }

  // Media Library
  addMediaFile(
    formData: any,
    category: 'content' | 'media' | 'any',
    errorHandler: any = undefined,
  ): Observable<any> {
    // return this.post(`sales-funnel/media/${category}`, [formData], errorHandler);
    return this.upload(
      `sales-funnel/media/${category}`,
      formData,
      errorHandler,
    );
  }

  getAllMedia(category = 'media', errorHandler: any = undefined) {
    return this.get(`sales-funnel/media`, errorHandler);
    // ?category=${category}
  }
  getMediaById(itemId: number, errorHandler: any = undefined) {
    return this.get(`sales-funnel/media/${itemId}`, {}, errorHandler);
  }
  getMediaTypes(errorHandler: any = undefined) {
    return this.get(`sales-funnel/media/types`, {}, errorHandler);
  }

  // Sales Funnel Calls End

  screenshot(url: string, fullPage: boolean, errorHandler: any = undefined) {
    return this.getScreenshot(
      'screen-shot?url=' + url + '&fullPage=' + fullPage,
      {},
      errorHandler,
    );
  }

  private post(path: string, params: any, errorHandler: any = undefined) {
    return this.call('post', path, params, errorHandler);
  }

  private patch(path: string, params: any, errorHandler: any = undefined) {
    return this.call('patch', path, params, errorHandler);
  }

  private get(path: string, params: any, errorHandler: any = undefined) {
    return this.call('get', path, params, errorHandler);
  }

  private getScreenshot(
    path: string,
    params: any,
    errorHandler: any = undefined,
  ) {
    return this.callScreenshot('get', path, params, errorHandler);
  }

  private put(path: string, params: any, errorHandler: any = undefined) {
    return this.call('put', path, params, errorHandler);
  }

  private delete(path: string, params: any, errorHandler: any = undefined) {
    return this.call('delete', path, params, errorHandler);
  }

  private download(
    path: string,
    params: any = undefined,
    errorHandler: any = undefined,
  ) {
    if (!errorHandler) errorHandler = this.handleError;
    return this.http
      .get<any>(`${this.apiUrl}/${path}`, {
        params,
        headers: this.headers,
        responseType: 'blob' as 'json',
      })
      .pipe(catchError(errorHandler));
  }

  public upload(path: string, formData, errorHandler: any = undefined) {
    if (!errorHandler) errorHandler = this.handleError;

    return this.http
      .request<any>('post', `${this.apiUrl}/${path}`, {
        body: formData,
      })
      .pipe(
        map((resp) => {
          if (resp?.error) {
            console.error('LSG API Error', resp);
            throw resp?.error;
          }
          return resp;
        }),
        catchError(errorHandler),
      );
  }

  call(method, path: string, params: any, errorHandler: any = undefined) {
    if (!errorHandler) errorHandler = this.handleError;
    // console.debug(`LSG API request '${method}'`, `${ this.apiUrl }/${ path }`, params);
    return this.http
      .request<any>(method, `${this.apiUrl}/${path}`, {
        body: params,
        headers: this.headers,
      })
      .pipe(
        map((resp) => {
          if (resp?.error) {
            console.error('LSG API Error', resp);
            throw resp?.error;
          }
          return resp;
        }),
        catchError(errorHandler),
      );
  }

  freeCall(method, url: string, params: any, errorHandler: any = undefined) {
    if (!errorHandler) errorHandler = this.handleError;
    // console.debug(`LSG API request '${method}'`, `${ this.apiUrl }/${ path }`, params);
    return this.http
      .request<any>(method, `${url}`, {
        body: params,
        headers: this.headers,
      })
      .pipe(
        map((resp) => {
          if (resp?.error) {
            console.error('LSG API Error', resp);
            throw resp?.error;
          }
          return resp;
        }),
        catchError(errorHandler),
      );
  }

  private handleError(error: HttpErrorResponse) {
    console.error('API SERVICE ERROR: ', error);
    this.prompting
      .fire(
        {
          title: 'Ooops, Something went wrong!',
          html: `
      learmo has encountered an error. If this problem persists,
contact us at help@influencerportal.com
      `,
          confirmButtonText: 'Reload',
          allowOutsideClick: true,
          allowEscapeKey: true,
          icon: 'error',
        },
        'md',
      )
      .then((result) => {
        if (result.isConfirmed) {
          document.location.reload();
        }
      });
    return throwError(
      'We were unable to connect to the Servers, please check your connection and try again.',
    );
  }

  callScreenshot(
    method,
    path: string,
    params: any,
    errorHandler: any = undefined,
  ) {
    if (!errorHandler) errorHandler = this.handleError;
    // console.debug(`LSG API request '${method}'`, `${ this.apiUrl }/${ path }`, params);

    return this.http
      .request<any>(method, `${this.apiUrl}/${path}`, {
        body: params,
        headers: this.headers,
      })
      .pipe(
        map((resp) => {
          if (resp?.error) {
            throw resp?.error;
          }
          return resp;
        }),
        catchError(errorHandler),
      );
  }
}
